import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IMeResponseDto, ISessionResponseDto } from '@shared';
import { map, Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { IOperationStatus } from '../models/operation-status.model';
import { ISession } from '../models/session.model';
import { IUserProfile } from '../models/user-profile.model';

@Injectable({ providedIn: 'root' })
export class ProfileApiService {
  #http = inject(HttpClient);
  #datePipe = inject(DatePipe);

  getUserProfile(): Observable<IUserProfile | null> {
    return this.#http
      .get<IMeResponseDto | null>(`${environment.apiUrl}/me`)
      .pipe(map((me) => this.#mapMeResponseToUserProfile(me)));
  }

  getSessions(): Observable<ISession[]> {
    return this.#http
      .get<ISessionResponseDto[]>(`${environment.apiUrl}/auth/sessions`)
      .pipe(
        map((sessions) =>
          sessions
            .filter((s) => !s.isRevoked)
            .map((s, index) => this.#mapToISession(s, index === 0)),
        ),
      );
  }

  revokeSession(sessionId: string): Observable<IOperationStatus> {
    return this.#http.post<IOperationStatus>(
      `${environment.apiUrl}/auth/sessions/${sessionId}/revoke`,
      {},
    );
  }

  revokeAllSessions(): Observable<IOperationStatus> {
    return this.#http.post<IOperationStatus>(
      `${environment.apiUrl}/auth/sessions/revoke-all`,
      {},
    );
  }

  #mapMeResponseToUserProfile(me: IMeResponseDto | null): IUserProfile | null {
    if (!me) return null;
    return {
      avatarUrl: me.picture ?? './default-avatar.webp',
      email: me.email,
      id: me.id,
      language: me.language,
      username: me.username,
    };
  }

  #mapToISession(session: ISessionResponseDto, isCurrent: boolean): ISession {
    const { browser, deviceType, os } = this.#parseUserAgent(session.userAgent);
    const icon = this.#getIconForDeviceType(deviceType);

    return {
      lastActive:
        this.#datePipe.transform(
          session.lastUsedAt || session.createdAt,
          'medium',
        ) || 'N/A',
      browser,
      icon,
      id: session.id,
      ip: session.ipAddress,
      isCurrent,
      os,
    };
  }

  #parseUserAgent(userAgent: null | string): {
    browser: string;
    os: string;
    deviceType: string;
  } {
    if (!userAgent) {
      return {
        browser: 'Unknown',
        deviceType: 'unknown',
        os: 'Unknown',
      };
    }

    const browser =
      [
        { keyword: 'Firefox', name: 'Firefox' },
        { keyword: 'Edg/', name: 'Edge' },
        { keyword: 'Chrome', name: 'Chrome' },
        { keyword: 'Safari', name: 'Safari' },
      ].find(({ keyword }) => userAgent.includes(keyword))?.name || 'Unknown';

    const osEntry = [
      { keyword: 'iPhone', name: 'iPhone', type: 'mobile' },
      { keyword: 'Android', name: 'Android', type: 'mobile' },
      { keyword: 'iPad', name: 'iPad', type: 'tablet' },
      { keyword: 'Macintosh', name: 'macOS', type: 'desktop' },
      { keyword: 'Windows', name: 'Windows', type: 'desktop' },
      { keyword: 'Linux', name: 'Linux', type: 'desktop' },
    ].find(({ keyword }) => userAgent.includes(keyword));

    return {
      browser,
      deviceType: osEntry?.type ?? 'unknown',
      os: osEntry?.name || 'Unknown',
    };
  }

  #getIconForDeviceType(deviceType: string): string {
    switch (deviceType) {
      case 'mobile':
        return 'phone_android';
      case 'tablet':
        return 'tablet_mac';
      case 'desktop':
        return 'desktop_windows';
      default:
        return 'device_unknown';
    }
  }
}
